// Shared UI for the research-wizard API-key guard: the "Verifying API keys…" banner shown while the
// probe is in flight, and the inline red note shown under a Data Source toggle whose key isn't valid.
// Language models, search providers, tearsheet, and Parallel.ai keys are not shown to organizations or members.
import { Link } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { ORG_ONLY_KEYS } from '@/features/apiKeys/catalog';
import { useAuthStore } from '@/store/authStore';
import type { OrgKeyName } from '@/types';

export function ApiKeyVerifyingBanner() {
  return (
    <div className="alert alert-info d-flex align-items-center mb-3" role="status">
      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
      <span>Verifying API keys…</span>
    </div>
  );
}

/** Shown when the key probe request itself failed (slow provider, deploy, network). Sources stay
 *  usable — the backend checks keys again when the run starts — and the user can re-probe. */
export function ApiKeyCheckFailedNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" role="status">
      <i className="bi bi-exclamation-circle" aria-hidden="true" />
      <span className="flex-grow-1">
        Couldn&apos;t verify API keys right now. Sources stay available, and keys are checked again
        when you start.
      </span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

export function configureApiKeyLink(keyName?: OrgKeyName): { to: string; label: string } | null {
  if (keyName === 'gmaps_api_key') return null;
  const user = useAuthStore.getState().currentUser;
  const orgManaged = !keyName || ORG_ONLY_KEYS.has(keyName);
  if (orgManaged) {
    if (user?.isAdmin) {
      return { to: paths.admin.adminOps, label: 'Open Admin Ops' };
    }
    return null;
  }
  return { to: paths.settings.apiKeys, label: 'Configure API Key' };
}

export function ApiKeyDisabledNote({ reason, keyName }: { reason: string; keyName?: OrgKeyName }) {
  if (!reason) return null;
  const link = configureApiKeyLink(keyName);
  return (
    <div className="text-danger small mb-2">
      <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
      {reason}
      {link ? (
        <>
          {' '}
          <Link to={link.to}>{link.label}</Link>
        </>
      ) : null}
    </div>
  );
}
